import React, { FC } from 'react'
import { ReactComponent as GreatRate } from 'assets/images/tags/great-rate.svg'
import { ReactComponent as Pallets } from 'assets/images/tags/pallets.svg'
import { ReactComponent as Popular } from 'assets/images/tags/popular.svg'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'

// withLabel determines whether a text description is included alongside the label icon.
interface ILoadsInsightsLabelProps {
  variant: string
  withLabel?: boolean
  isMobile?: boolean
}

interface ITypeContent {
  background: string
  mobileTextColor: string
  tooltipText: string
  iconTag: JSX.Element
  text: JSX.Element
}

interface IInformationalContent {
  [variant: string]: ITypeContent
}

const informationalContent: IInformationalContent = {
  FCFS: {
    background: brandColors.skyBlue2,
    mobileTextColor: brandColors.skyBlue7,
    tooltipText: 'Open Window',
    iconTag: <Pallets />,
    text: (
      <>
        <strong>Open window!</strong> Get in, get out and keep moving. Book soon!
      </>
    ),
  },
  popular_shipment: {
    background: brandColors.purple0,
    mobileTextColor: brandColors.purple1,
    tooltipText: 'Popular Load',
    iconTag: <Popular />,
    text: (
      <>
        <strong>Popular load!</strong> Many carriers are bidding on this load. Book soon!
      </>
    ),
  },
  great_rate_shipment: {
    background: brandColors.green1,
    mobileTextColor: brandColors.green3,
    tooltipText: 'Great Rate',
    iconTag: <GreatRate />,
    text: (
      <>
        <strong>Great rate!</strong> Loads with rates like this won’t last long. Book soon!
      </>
    ),
  },
}

const useLoadsInsightsLabelStyles = makeStyles(() => ({
  fullContainer: (props: ILoadsInsightsLabelProps) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 24,

    '& .content': {
      fontSize: 14,
      paddingLeft: 12,
      width: 'calc(100% - 40px)',
    },

    '& .iconTag': {
      position: 'relative',
      background: informationalContent[props.variant]?.background,
      borderRadius: '100%',
      width: 40,
      height: 40,
      padding: 4,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',

      '& svg': {
        width: 18,
        height: 18,
      },
    },
  }),
  container: (props: ILoadsInsightsLabelProps) => ({
    position: 'relative',
    background: informationalContent[props.variant]?.background,
    borderRadius: '100%',
    width: 24,
    height: 24,
    padding: 4,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    '&.mobileContainer': {
      flexDirection: 'row',
      width: 'auto',
      gap: 7,
      borderRadius: 44,
      padding: '4px 6px',
      fontWeight: 600,
      color: informationalContent[props.variant]?.mobileTextColor,
    },

    '& svg': {
      width: 16,
      height: 16,
    },

    '&:before': {
      content: '" "',
      display: 'none',
      position: 'absolute',
      zIndex: 9998,
      top: -10,
      left: 5,
      width: 0,
      height: 0,
      borderLeft: '5px solid transparent',
      borderRight: '5px solid transparent',
      borderTop: `5px solid ${brandColors.coolGray7}`,
    },

    '&:after': {
      content: `"${informationalContent[props.variant]?.tooltipText}!"`,
      display: 'none',
      position: 'absolute',
      zIndex: 9999,
      top: -46,
      left: -46,
      width: 114,
      height: 36,
      color: '#F1F3F5',
      fontWeight: 700,
      fontSize: 12,
      lineHeight: 3,
      textAlign: 'center',
      background: brandColors.coolGray7,
      borderRadius: 4,
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    },

    '&:hover': {
      '&:before, &:after': {
        display: 'block',
      },
    },
  }),
}))

export const LoadsInsightsLabel: FC<ILoadsInsightsLabelProps> = ({
  isMobile = false,
  variant = '',
  withLabel = false,
}) => {
  const classes = useLoadsInsightsLabelStyles({ variant, withLabel })

  if (informationalContent[variant]) {
    return (
      <>
        {withLabel ? (
          <div className={`${classes.container} ${isMobile && 'mobileContainer'}`}>
            {informationalContent[variant]?.iconTag}
            {isMobile && informationalContent[variant]?.tooltipText}
          </div>
        ) : (
          <div className={classes.fullContainer}>
            <div className='iconTag'> {informationalContent[variant]?.iconTag}</div>
            <div className='content'>{informationalContent[variant]?.text}</div>
          </div>
        )}
      </>
    )
  }

  return null
}
